import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaPlus, FaSearch, FaExpand, FaCompress, FaTrash, FaCopy, FaShare, FaSortAmountUp, FaSortAmountDown, FaDownload, FaExclamationTriangle, FaTag, FaClock, FaFileExport } from 'react-icons/fa';
import '../styles/PromptManagerPage.css';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import CreateContentForm from '../components/CreateContentForm';
import ViewContentForm from '../components/ViewContentForm';
import SearchBar from '../components/SearchBar';
import TypeFilters from '../components/TypeFilters';
import ContentCard from '../components/ContentCard';
import SortControls from '../components/SortControls';
import CreateButton from '../components/CreateButton';
import LoadingGrid from '../components/LoadingGrid';
import UploadButton from '../components/UploadButton';
import { API_URL } from '../config';
import ShareConfirmDialog from '../components/ShareConfirmDialog';
import { contentService } from '../services/contentService';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

function PromptManagerPage() {
  const { getToken } = useAuth();
  const [contents, setContents] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showSharedOnly, setShowSharedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showNewContentForm, setShowNewContentForm] = useState(false);
  const [showNewTypeForm, setShowNewTypeForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [contentHistory, setContentHistory] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [sortField, setSortField] = useState('updated_at');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showBulkCategoryMenu, setShowBulkCategoryMenu] = useState(false);
  const [bulkCategoryMenuPosition, setBulkCategoryMenuPosition] = useState({ top: 0, left: 0 });
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const updateCategoryButtonRef = useRef(null);
  const bulkCategoryMenuRef = useRef(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  
  // Track screen width for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add pagination state
  const [totalCount, setTotalCount] = useState(0);

  // Initialize API state
  const [initialized, setInitialized] = useState(false);
  
  // Form states for new content
  const [newContent, setNewContent] = useState({
    title: '',
    description: '',
    content: '',
    content_type: '',
    state: 'Draft'
  });

  // Add state for default content type
  const [defaultContentType, setDefaultContentType] = useState(null);

  const [shareConfirmContent, setShareConfirmContent] = useState(null);
  
  // Mobile navigation state
  const [showMobileNav, setShowMobileNav] = useState(false);

  // Touch handlers for mobile navigation modal
  const [touchStartY, setTouchStartY] = useState(0);
  const [touchCurrentY, setTouchCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
    setTouchCurrentY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    setTouchCurrentY(currentY);
    
    const deltaY = currentY - touchStartY;
    
    // Only allow downward dragging
    if (deltaY > 0) {
      const modal = e.currentTarget;
      const translateY = Math.min(deltaY, 100); // Limit the drag distance
      modal.style.transform = `translateY(${translateY}px)`;
    }
  };

  const handleTouchEnd = (e) => {
    if (!isDragging) return;
    
    const deltaY = touchCurrentY - touchStartY;
    
    // If dragged down more than 100px, close the modal
    if (deltaY > 100) {
      setShowMobileNav(false);
    } else {
      // Reset the modal position
      const modal = e.currentTarget;
      modal.style.transform = 'translateY(0)';
    }
    
    setIsDragging(false);
    setTouchStartY(0);
    setTouchCurrentY(0);
  };

  // Navigation items for mobile menu
  const navigationItems = [
    {
      id: 'library',
      title: 'Library',
      path: '/prompt-manager',
      icon: '/images/library.png',
      iconGif: '/images/library.gif'
    },
    {
      id: 'tone-shift',
      title: 'Tone Shift',
      path: '/tone-shift',
      icon: '/images/toneshift.png',
      iconGif: '/images/toneshift.gif'
    },
    {
      id: 'chat',
      title: 'Chat',
      path: '/chat',
      icon: '/images/chat.png',
      iconGif: '/images/chat.gif'
    },
    {
      id: 'workflows',
      title: 'Workflows',
      path: '/workflows',
      icon: '/images/workflows.png',
      iconGif: '/images/workflows.gif'
    },
    {
      id: 'prototyper',
      title: 'Prototyper',
      path: '/prototyper',
      icon: '/images/prototype.png',
      iconGif: '/images/prototype.gif'
    }
  ];

  const [showShareDialog, setShowShareDialog] = useState(false);

  const { user } = useAuth();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
  });

  useEffect(() => {
    const initializeData = async () => {
      try {
        await fetchContentTypes();
        setInitialized(true);
      } catch (error) {
        console.error('Error initializing data:', error);
        setError('Failed to initialize application');
      }
    };
    
    initializeData();
  }, []);

  useEffect(() => {
    if (initialized) {
      setPage(1);
      setHasMore(true);
      fetchContents(1);
    }
  }, [initialized, debouncedSearchQuery, selectedTypes, sortBy, sortOrder, showSharedOnly]);

  const fetchContentTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content-types/`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch content types');
      const data = await response.json();
      setContentTypes(data);
      
      // Find and set the default "Unassigned" content type
      const unassignedType = data.find(type => type.name === "Unassigned");
      if (unassignedType) {
        setDefaultContentType(unassignedType);
        // Set default content type for new content form
        setNewContent(prev => ({
          ...prev,
          content_type: unassignedType.id.toString()
        }));
      }
    } catch (error) {
      console.error('Error fetching content types:', error);
      toast.error('Failed to fetch content types');
    }
  };

  const fetchContents = async (pageNum = page) => {
    try {
      setLoading(pageNum === 1);
      if (pageNum > 1) setLoadingMore(true);

      const queryParams = new URLSearchParams({
        page: pageNum,
        page_size: pageSize,
        order_by: sortBy,
        order_dir: sortOrder,
        search: debouncedSearchQuery,
        shared_only: showSharedOnly
      });

      if (selectedTypes.length > 0) {
        selectedTypes.forEach(type => queryParams.append('content_type', type));
      }

  
      
      const response = await fetch(`${API_URL}/api/contents/?${queryParams}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to fetch contents: ${errorData.message || response.statusText}`);
      }
      
      const data = await response.json();
      const results = Array.isArray(data) ? data : (data.results || []);
      

      
      const totalItems = Array.isArray(data) ? data.length : (data.total_count || 0);
      const totalPagesCount = Math.ceil(totalItems / pageSize);
      
      if (pageNum === 1) {
        setContents(results);
      } else if (results.length > 0) {
        // Only append if we got new results
        setContents(prev => {
          // Create a Set of existing IDs for deduplication
          const existingIds = new Set(prev.map(content => content.id));
          // Filter out any duplicates from new results
          const newContents = results.filter(content => !existingIds.has(content.id));
          return [...prev, ...newContents];
        });
      }
      
      setTotalCount(totalItems);
      setTotalPages(totalPagesCount);
      // Update hasMore based on whether we're on the last page and if we got results
      setHasMore(pageNum < totalPagesCount && results.length === pageSize);
      setPage(pageNum);
      setError(null);
    } catch (error) {
      console.error('Error in fetchContents:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchContents(page + 1);
    }
  };

  // Update the intersection observer to be more precise
  const observer = useRef();
  const lastContentElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        handleLoadMore();
      }
    }, {
      threshold: 0.5 // Only trigger when 50% of the element is visible
    });
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);

  const fetchContentHistory = async (contentId) => {
    try {
      const response = await fetch(`${API_URL}/api/content-history/?content_id=${contentId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch content history');
      const data = await response.json();
      setContentHistory(data);
    } catch (error) {
      console.error('Error fetching content history:', error);
    }
  };

  const loadVersionHistory = async () => {
    if (!selectedContent) return;
    
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/api/content-history/?content_id=${selectedContent.id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch content history');
      const data = await response.json();
      setContentHistory(data);
    } catch (error) {
      console.error('Error fetching content history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleContentClick = async (content) => {
    try {
      // Fetch the content with its references
      const response = await fetch(`${API_URL}/api/contents/${content.id}/`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch content details');
      const contentWithRefs = await response.json();
      
      setSelectedContent(contentWithRefs);
      setShowEditForm(true);
      setShowVersionHistory(false);
      setContentHistory([]);
    } catch (error) {
      console.error('Error fetching content details:', error);
      toast.error('Failed to load content details');
    }
  };

  // Then calculate type counts from filtered contents
  const typeCountMap = (contents || []).reduce((acc, content) => {
    if (content && content.content_type && content.content_type.id) {
      const typeId = content.content_type.id;
      acc[typeId] = (acc[typeId] || 0) + 1;
    }
    return acc;
  }, {});

  // Add counts to content types
  const typesWithCounts = (contentTypes || []).map(type => ({
    ...type,
    count: typeCountMap[type.id] || 0
  }));

  // Finally sort the filtered contents
  const sortedContents = Array.isArray(contents) ? [...contents].sort((a, b) => {
    if (!a || !b) return 0;
    const aValue = sortBy === 'updated_at' ? a.updated_at : 
                   sortBy === 'created_at' ? a.created_at : 
                   (a.title || '');
    const bValue = sortBy === 'updated_at' ? b.updated_at : 
                   sortBy === 'created_at' ? b.created_at : 
                   (b.title || '');
    return sortOrder === 'asc' ? 
           (aValue > bValue ? 1 : aValue < bValue ? -1 : 0) : 
           (aValue < bValue ? 1 : aValue > bValue ? -1 : 0);
  }) : [];

  // Add function to compute differences between versions
  const computeDiff = (currentVersion, previousVersion) => {
    if (!previousVersion) return null;

    const differences = {};
    const fields = ['title', 'description', 'content_body', 'content_type', 'state'];

    fields.forEach(field => {
      let oldValue = previousVersion[field];
      let newValue = currentVersion[field];

      // Handle nested objects
      if (field === 'content_type' || field === 'state') {
        oldValue = previousVersion[field]?.name;
        newValue = currentVersion[field]?.name;
      }

      // Handle content_body field
      if (field === 'content_body') {
        if (oldValue !== newValue) {
          differences['content'] = {
            old: oldValue || '',
            new: newValue || ''
          };
        }
      } else if (oldValue !== newValue) {
        differences[field] = {
          old: oldValue || '',
          new: newValue || ''
        };
      }
    });

    return Object.keys(differences).length > 0 ? differences : null;
  };

  // Update the diff display
  const renderDiff = (field, changes) => {
    const fieldName = field === 'content_body' ? 'Content' : 
      field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ');

    return (
      <div key={field} className="diff-item">
        <div className="diff-field">{fieldName}</div>
        <div className="diff-content">
          <div className="diff-old">
            <div className="diff-header">Previous Version</div>
            <pre className="diff-text">{changes.old || 'No content'}</pre>
          </div>
          <div className="diff-new">
            <div className="diff-header">New Version</div>
            <pre className="diff-text">{changes.new || 'No content'}</pre>
          </div>
        </div>
      </div>
    );
  };

  // Update version history section in the form
  const renderVersionHistory = () => (
    <div className="version-history">
      <h3>Version History</h3>
      <div className="version-table">
        <table>
          <thead>
            <tr>
              <th>Version</th>
              <th>Title</th>
              <th>Modified</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contentHistory.map((version, index) => {
              const previousVersion = contentHistory[index + 1];
              const diff = computeDiff(version, previousVersion);
              const isExpanded = expandedVersion === version.id;
              
              return (
                <React.Fragment key={version.id}>
                  <tr>
                    <td>{version.version}</td>
                    <td>{version.title}</td>
                    <td>{new Date(version.created_at).toLocaleString()}</td>
                    <td>
                      <button
                        className="view-diff-button"
                        onClick={() => setExpandedVersion(isExpanded ? null : version.id)}
                        disabled={!diff}
                      >
                        {isExpanded ? 'Hide Changes' : 'View Changes'}
                      </button>
                    </td>
                  </tr>
                  {isExpanded && diff && (
                    <tr>
                      <td colSpan="4">
                        <div className="version-diff">
                          {Object.entries(diff).map(([field, changes]) => renderDiff(field, changes))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Add delete handler
  const handleDelete = async (contentId, e) => {
    if (e) e.preventDefault();
    
    toast.promise(
      new Promise((resolve, reject) => {
        const toastId = toast.error(
          <div>
            <p>Are you sure you want to delete this content?</p>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={async () => {
                  toast.dismiss(toastId);
                  try {
                    const response = await fetch(`${API_URL}/api/contents/${contentId}/`, {
                      method: 'DELETE',
                      headers: getAuthHeaders(),
                    });
                    
                    if (!response.ok) throw new Error('Failed to delete content');
                    
                    await fetchContents();
                    setShowEditForm(false);
                    resolve();
                  } catch (error) {
                    console.error('Error deleting content:', error);
                    reject(error);
                  }
                }}
                className="toast-button delete"
              >
                Yes, delete
              </button>
              <button
                onClick={() => {
                  toast.dismiss(toastId);
                  reject('Cancelled');
                }}
                className="toast-button cancel"
              >
                Cancel
              </button>
            </div>
          </div>,
          { autoClose: false, closeButton: false }
        );
      }),
      {
        pending: 'Deleting content...',
        success: 'Content deleted successfully',
        error: {
          render: ({ data }) => {
            return data === 'Cancelled' 
              ? 'Delete cancelled' 
              : 'Failed to delete content';
          }
        }
      }
    );
  };

  // Add the version history toggle button and loading state to the form
  const renderVersionHistorySection = () => (
    <div className="version-history-section">
      <button 
        className="version-history-toggle"
        onClick={() => {
          if (!showVersionHistory) {
            loadVersionHistory();
          }
          setShowVersionHistory(!showVersionHistory);
        }}
      >
        {showVersionHistory ? (
          <>
            <span className="emoji">👁️</span> Hide Version History
          </>
        ) : (
          <>
            <span className="emoji">👁️</span> Show Version History
          </>
        )}
      </button>
      
      {showVersionHistory && (
        <div className="version-history-content">
          {loadingHistory ? (
            <div className="version-history-skeleton">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="history-item-skeleton">
                  <div className="skeleton-header">
                    <div className="skeleton-version" />
                    <div className="skeleton-date" />
                  </div>
                  <div className="skeleton-content">
                    <div className="skeleton-line" />
                    <div className="skeleton-line" />
                    <div className="skeleton-line short" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            renderVersionHistory()
          )}
        </div>
      )}
    </div>
  );

  // Add new function to process content
  const processContent = (content) => {
    if (!content.content || !Array.isArray(contents)) {
      return content.content || '';
    }

    let processedContent = content.content;
    
    // Handle both old and new format reference markers
    const oldContextPattern = /\[@(\d+)\]/g;
    const newContextPattern = /~~\[@(\d+)\]~~(?:\s|$)/g;

    // Process old format markers
    processedContent = processedContent.replace(oldContextPattern, (match, contextId) => {
      const referencedContent = contents.find(c => c.id === parseInt(contextId, 10));
      return referencedContent ? referencedContent.content : match;
    });

    // Process new format markers
    processedContent = processedContent.replace(newContextPattern, (match, contextId) => {
      const referencedContent = contents.find(c => c.id === parseInt(contextId, 10));
      return referencedContent ? referencedContent.content + ' ' : match;
    });

    return processedContent;
  };

  // Update the copy handler to use processed content
  const handleCopy = async (e, content) => {
    e.stopPropagation();
    try {
      const processedContent = processContent(content);
      await navigator.clipboard.writeText(processedContent);
      toast.success('Content copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy content');
    }
  };

  // Add new function to handle sharing
  const handleShare = async (e, content) => {
    e.stopPropagation();
    
    // If content already has a share link, just copy it
    if (content.shared_links && content.shared_links.length > 0) {
      const shareId = content.shared_links[0].id;
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/#/share/${shareId}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
      return;
    }
    
    // Otherwise, show confirmation dialog
    setShareConfirmContent(content);
    setShowShareDialog(true);
  };

  const handleShareConfirm = async (content) => {
    try {
      const data = await contentService.shareContent(content.id);
      if (!data || !data.share_id) {
        throw new Error('Invalid share response');
      }
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/#/share/${data.share_id}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
      setShowShareDialog(false);
      fetchContents();
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to share content');
      setShowShareDialog(false);
    }
  };

  const handleBulkDelete = async (contentIds) => {
    toast.promise(
      new Promise((resolve, reject) => {
        const toastId = toast.error(
          <div>
            <p>Are you sure you want to delete {contentIds.length} items?</p>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={async () => {
                  toast.dismiss(toastId);
                  try {
                    // Delete each content item
                    await Promise.all(contentIds.map(id => 
                      fetch(`${API_URL}/api/contents/${id}/`, {
                        method: 'DELETE',
                        headers: getAuthHeaders(),
                      })
                    ));
                    
                    await fetchContents();
                    setSelectedCards(new Set()); // Clear selection
                    resolve();
                  } catch (error) {
                    console.error('Error deleting contents:', error);
                    reject(error);
                  }
                }}
                className="toast-button delete"
              >
                Yes, delete all
              </button>
              <button
                onClick={() => {
                  toast.dismiss(toastId);
                  reject('Cancelled');
                }}
                className="toast-button cancel"
              >
                Cancel
              </button>
            </div>
          </div>,
          { autoClose: false, closeButton: false }
        );
      }),
      {
        pending: 'Deleting contents...',
        success: 'Contents deleted successfully',
        error: {
          render: ({ data }) => {
            return data === 'Cancelled' 
              ? 'Delete cancelled' 
              : 'Failed to delete contents';
          }
        }
      }
    );
  };

  const handleBulkExport = async (contentIds) => {
    try {
      // Filter contents to get selected ones
      const selectedContents = contents.filter(content => contentIds.includes(content.id));
      
      // Format the data for export
      const exportData = selectedContents.map(content => ({
        title: content.title,
        description: content.description,
        content: content.content,
        type: content.content_type.name,
        state: content.state.name,
        created_at: content.created_at,
        updated_at: content.updated_at
      }));

      // Create a Blob with the JSON data
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `prompt-parker-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`${contentIds.length} items exported successfully`);
    } catch (error) {
      console.error('Error exporting contents:', error);
      toast.error('Failed to export contents');
    }
  };

  // Add these functions back after the sorting logic

  const handleTypeSelect = (typeId) => {
    setSelectedTypes(prevTypes => {
      if (prevTypes.includes(typeId)) {
        return prevTypes.filter(id => id !== typeId);
      } else {
        return [...prevTypes, typeId];
      }
    });
  };

  const handleUpdateContent = async (updatedContent) => {
    try {
      // Ensure content_type is properly parsed as an integer
      const contentTypeId = parseInt(updatedContent.content_type, 10);
      
      if (isNaN(contentTypeId)) {
        console.error('Invalid content type ID:', updatedContent.content_type);
        throw new Error('Invalid content type selected');
      }
      
  
      
      // Create the request body
      const requestBody = {
        title: updatedContent.title,
        description: updatedContent.description,
        content: updatedContent.content,
        content_type_id: contentTypeId,
        state_id: updatedContent.state?.id || updatedContent.state_id || 1,
        contextReferences: updatedContent.contextReferences || []
      };
      
  
      
      // First, get the current content to compare
      const getResponse = await fetch(`${API_URL}/api/contents/${updatedContent.id}/`, {
        headers: getAuthHeaders()
      });
      
      if (!getResponse.ok) {
        throw new Error('Failed to fetch current content');
      }
      
      const currentContent = await getResponse.json();
      
      // Now update the content
      const response = await fetch(`${API_URL}/api/contents/${updatedContent.id}/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('API error response:', errorData);
        throw new Error(errorData.message || 'Failed to update content');
      }
      
      // Get the updated content directly from the API
      const getUpdatedResponse = await fetch(`${API_URL}/api/contents/${updatedContent.id}/`, {
        headers: getAuthHeaders()
      });
      
      if (!getUpdatedResponse.ok) {
        throw new Error('Failed to fetch updated content');
      }
      
      const updatedContentFromApi = await getUpdatedResponse.json();

      
      await fetchContents();
      toast.success('Content updated successfully!');
      setShowEditForm(false);
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error(error.message);
    }
  };

  const handleCreateContent = async (formData) => {
    try {
      const contentTypeId = parseInt(formData.content_type_id, 10) || defaultContentType?.id;
      if (!contentTypeId) {
        throw new Error('Content type is required');
      }

      const response = await fetch(`${API_URL}/api/contents/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          content: formData.content,
          content_type_id: contentTypeId,
          state_id: 1,
          contextReferences: formData.contextReferences || []
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        throw new Error(errorData.message || 'Failed to create content');
      }
      
      setShowNewContentForm(false);
      await fetchContents();
      toast.success('Content created successfully!');
    } catch (error) {
      console.error('Error creating content:', error);
      toast.error(error.message);
    }
  };

  const handleFileUpload = async (contentItems) => {
    try {
      // Find or create content types for categories
      const categoryMap = new Map();
      for (const item of contentItems) {
        if (item.category && !categoryMap.has(item.category)) {
          const existingType = contentTypes.find(
            type => type.name.toLowerCase() === item.category.toLowerCase()
          );
          if (existingType) {
            categoryMap.set(item.category, existingType.id);
          }
        }
      }

      // Create content items
      const createdItems = await Promise.all(
        contentItems.map(async item => {
          const contentTypeId = categoryMap.get(item.category) || defaultContentType?.id;
          
          const requestBody = {
            title: item.title || 'Untitled',
            description: item.description || '',
            content: item.content,
            content_type_id: contentTypeId,
            state_id: 1
          };

          const response = await fetch(`${API_URL}/api/contents/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(requestBody)
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Failed to create content: ${item.title}`);
          }
          return response.json();
        })
      );

      await fetchContents();
      toast.success(`Successfully imported ${createdItems.length} items`);
    } catch (error) {
      console.error('Error uploading content:', error);
      toast.error('Failed to import content');
    }
  };

  // Calculate shared count
  const sharedCount = (contents || []).filter(content => 
    content && content.shared_links && content.shared_links.length > 0
  ).length;

  // Update clear filters to also clear shared filter
  const handleClearFilters = () => {
    setSelectedTypes([]);
    setShowSharedOnly(false);
  };
  
  // Update the function to toggle the bulk category menu
  const toggleBulkCategoryMenu = () => {
    if (!showBulkCategoryMenu && updateCategoryButtonRef.current) {
      const rect = updateCategoryButtonRef.current.getBoundingClientRect();
      // Position the menu above the button
      setBulkCategoryMenuPosition({
        top: rect.top, // Use the top of the button
        left: rect.left
      });
    }
    setShowBulkCategoryMenu(!showBulkCategoryMenu);
  };

  // Add bulk category update function
  const handleBulkCategoryUpdate = async (contentTypeId) => {
    if (selectedCards.size === 0) return;
    
    try {
      setLoading(true);
      
      // Create an array of promises for each update
      const updatePromises = [...selectedCards].map(async (contentId) => {
        const response = await fetch(`${API_URL}/api/contents/${contentId}/`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content_type_id: contentTypeId
          })
        });
        
        if (!response.ok) {
          throw new Error(`Failed to update content ${contentId}`);
        }
        
        return await response.json();
      });
      
      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      // Refresh the content list
      await fetchContents();
      
      // Clear selections
      setSelectedCards(new Set());
      setShowBulkCategoryMenu(false);
      
      toast.success(`Updated ${selectedCards.size} items to new category`);
    } catch (error) {
      console.error('Error updating categories:', error);
      toast.error('Failed to update categories');
    } finally {
      setLoading(false);
    }
  };

  // Initialize contents state properly
  useEffect(() => {
    setContents([]);  // Ensure contents is always an array
  }, []);

  return (
    <div className="prompt-manager-page">
      {/* Mobile Header */}
      <div className="mobile-header">
        <div className="mobile-header-content">
          {/* Mobile hamburger menu */}
          <button
            className="mobile-hamburger-btn"
            onClick={() => setShowMobileNav(true)}
            aria-label="Open navigation menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <h1>
            <img src="/images/library.png" alt="Library" className="header-title-icon" />
            Library
          </h1>
        </div>
      </div>

      <div className="top-controls">
        <SearchBar value={searchQuery} onChange={setSearchQuery} />
        <div className="action-buttons">
          <UploadButton onUpload={handleFileUpload} />
          <CreateButton onClick={() => setShowNewContentForm(true)} />
        </div>
      </div>

      <div className="filter-sort-section">
        <TypeFilters 
          types={typesWithCounts}
          selectedTypes={selectedTypes}
          onTypeSelect={handleTypeSelect}
          onClearFilters={handleClearFilters}
          showSharedOnly={showSharedOnly}
          onToggleShared={() => setShowSharedOnly(!showSharedOnly)}
          sharedCount={sharedCount}
        />
        <SortControls 
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortByChange={setSortBy}
          onSortOrderChange={() => setSortOrder(order => order === 'asc' ? 'desc' : 'asc')}
        />
      </div>

      {loading && page === 1 ? (
        <LoadingGrid />
      ) : error ? (
        <div className="error-state">
          <FaExclamationTriangle />
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button onClick={() => fetchContents(1)}>Try Again</button>
        </div>
      ) : contents.length === 0 ? (
        <div className="empty-state">
          <h3>No Content Found</h3>
          <p>Start by creating your first prompt!</p>
          <button
            className="action-button"
            onClick={() => setShowNewContentForm(true)}
          >
            <FaPlus /> Create
          </button>
        </div>
      ) : (
        <>
          <div className="content-grid">
            {contents.map((content, index) => (
              <div
                key={content.id}
                ref={index === contents.length - 1 ? lastContentElementRef : null}
              >
                <ContentCard
                  content={content}
                  onSelect={handleContentClick}
                  onCopy={handleCopy}
                  onShare={handleShare}
                  isSelected={selectedCards.has(content.id)}
                  onCheckboxChange={(id, checked) => {
                    setSelectedCards(prev => {
                      const next = new Set(prev);
                      if (checked) next.add(id);
                      else next.delete(id);
                      return next;
                    });
                  }}
                />
              </div>
            ))}
          </div>
          {loadingMore && (
            <div className="loading-more">
              <div className="loading-spinner"></div>
              Loading more...
            </div>
          )}
        </>
      )}

      {selectedCards.size > 0 && (
        <div className={`bulk-actions-bar visible ${isMobile ? 'mobile-bulk-actions' : ''}`}>
          <span className="bulk-actions-count">
            {selectedCards.size} items selected
          </span>
          <div className="bulk-actions">
            <div style={{ position: 'relative' }}>
              <button 
                ref={updateCategoryButtonRef}
                onClick={toggleBulkCategoryMenu}
                className="category-button"
              >
                <FaTag /> Update Category <span style={{ marginLeft: '4px', fontSize: '10px' }}>▼</span>
              </button>
            </div>
            <button className="export" onClick={() => handleBulkExport([...selectedCards])}>
              <FaFileExport /> Export
            </button>
            <button className="delete" onClick={() => handleBulkDelete([...selectedCards])}>
              <FaTrash /> Delete
            </button>
          </div>
        </div>
      )}

      {/* Render the bulk category menu outside the bulk actions bar */}
      {showBulkCategoryMenu && (
        <div 
          className="bulk-category-menu-overlay"
          onClick={() => setShowBulkCategoryMenu(false)}
        >
          <div 
            className="bulk-category-menu"
            style={{
              position: 'fixed',
              bottom: `${window.innerHeight - bulkCategoryMenuPosition.top + 10}px`,
              left: bulkCategoryMenuPosition.left + 'px'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="bulk-category-menu-header">
              <h4>Select Category</h4>
              <button 
                className="close-menu-button"
                onClick={() => setShowBulkCategoryMenu(false)}
              >
                ×
              </button>
            </div>
            <div className="bulk-category-options">
              {contentTypes.map(type => (
                <button
                  key={type.id}
                  className="category-option"
                  onClick={() => {
                    handleBulkCategoryUpdate(type.id);
                    setShowBulkCategoryMenu(false);
                  }}
                >
                  {type.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <ViewContentForm 
        isOpen={showEditForm}
        onClose={() => setShowEditForm(false)}
        content={selectedContent}
        contentTypes={contentTypes}
        onUpdate={handleUpdateContent}
        savedContents={contents}
        onViewHistory={() => {
          setShowVersionHistory(true);
          loadVersionHistory();
        }}
      />

      <CreateContentForm 
        isOpen={showNewContentForm}
        onClose={() => setShowNewContentForm(false)}
        onSubmit={handleCreateContent}
        contentTypes={contentTypes}
        defaultContentType={defaultContentType}
        savedContents={contents}
      />

      <ShareConfirmDialog 
        isOpen={showShareDialog}
        onClose={() => {
          setShowShareDialog(false);
          setShareConfirmContent(null);
        }}
        onConfirm={() => {
          if (shareConfirmContent) {
            handleShareConfirm(shareConfirmContent);
          }
        }}
        title={shareConfirmContent?.title}
      />

      {/* Mobile Navigation Bottom Sheet */}
      {showMobileNav && (
        <>
          <div 
            className="dropdown-backdrop show" 
            onClick={() => setShowMobileNav(false)}
          />
          <div 
            className="mobile-nav-modal show"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Gripper handle */}
            <div className="modal-gripper">
              <div className="gripper-line"></div>
            </div>
            <div className="mobile-nav-header">
              <h3>Navigation</h3>
              <button 
                onClick={() => setShowMobileNav(false)}
                className="mobile-nav-close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="mobile-nav-list">
              {navigationItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className="mobile-nav-item"
                  onClick={() => setShowMobileNav(false)}
                >
                  <img 
                    src={item.icon} 
                    alt={`${item.title} Icon`}
                    className="mobile-nav-icon"
                  />
                  <span className="mobile-nav-title">{item.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PromptManagerPage;
